import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, SERVICES, SOFTWARE, REFERENCES, COMPANY } from "@/lib/data";
import { useDemoModal } from "@/components/DemoModalContext";
import * as Icons from "lucide-react";

const Icon = ({ name, className }) => {
  const Cmp = Icons[name] || Icons.Circle;
  return <Cmp className={className} strokeWidth={1.6} />;
};

const MegaPanel = ({ type }) => {
  if (type === "software") {
    return (
      <div className="grid grid-cols-2 gap-2 w-[560px]" data-testid="mega-software">
        {SOFTWARE.map((s) => (
          <a
            key={s.title}
            href="#yazilimlar"
            className="group flex items-start gap-3 rounded-xl p-3 transition-colors duration-200 hover:bg-cloud"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-orange/10 text-orange transition-colors group-hover:bg-orange group-hover:text-white">
              <Icon name={s.icon} className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-ink">{s.title}</span>
              <span className="block truncate text-xs text-ink/55">{s.tag}</span>
            </span>
          </a>
        ))}
      </div>
    );
  }
  if (type === "services") {
    return (
      <div className="grid grid-cols-2 gap-2 w-[560px]" data-testid="mega-services">
        {SERVICES.slice(0, 8).map((s) => (
          <a
            key={s.title}
            href="#hizmetler"
            className="group flex items-start gap-3 rounded-xl p-3 transition-colors duration-200 hover:bg-cloud"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-lime/15 text-[#7fa021] transition-colors group-hover:bg-lime group-hover:text-white">
              <Icon name={s.icon} className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-ink">{s.title}</span>
              <span className="block truncate text-xs text-ink/55">{s.desc.slice(0, 34)}…</span>
            </span>
          </a>
        ))}
      </div>
    );
  }
  // refs
  return (
    <div className="w-[420px]" data-testid="mega-refs">
      <p className="mb-3 px-3 text-xs font-bold uppercase tracking-[0.2em] text-orange">
        Güvenilir İş Ortaklarımız
      </p>
      <div className="grid grid-cols-2 gap-2">
        {REFERENCES.slice(0, 6).map((r) => (
          <a
            key={r.name}
            href="#referanslar"
            className="rounded-xl px-3 py-2 transition-colors duration-200 hover:bg-cloud"
          >
            <span className="block text-sm font-semibold text-ink">{r.name}</span>
            <span className="block text-xs text-ink/55">{r.sector}</span>
          </a>
        ))}
      </div>
    </div>
  );
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMega, setActiveMega] = useState(null);
  const { setOpen } = useDemoModal();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
          scrolled
            ? "border-b border-ink/5 bg-white/75 shadow-[0_4px_30px_rgba(0,0,0,0.05)] backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
        data-testid="navbar"
      >
        <div className="mx-auto flex h-[76px] max-w-[1440px] items-center justify-between px-6 md:px-[100px]">
          {/* Logo */}
          <a href="#hero" className="flex items-center gap-2" data-testid="navbar-logo">
            <img
              src="/logo.png"
              alt="TEKNO ŞURA"
              className={`h-12 w-auto transition-all duration-300 md:h-14 ${
                scrolled ? "" : "drop-shadow-[0_2px_10px_rgba(0,0,0,0.35)]"
              }`}
            />
          </a>

          {/* Center nav */}
          <nav
            className="pointer-events-auto absolute left-1/2 hidden -translate-x-1/2 items-center gap-1 lg:flex"
            onMouseLeave={() => setActiveMega(null)}
          >
            {NAV_LINKS.map((link) => {
              const dark = !scrolled;
              return (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={() => setActiveMega(link.mega || null)}
                >
                  <a
                    href={link.href}
                    aria-expanded={link.mega ? activeMega === link.mega : undefined}
                    className={`relative rounded-full px-3.5 py-2 text-sm font-medium transition-colors duration-200 ${
                      dark
                        ? "text-white/90 hover:text-white"
                        : "text-ink/70 hover:text-ink"
                    }`}
                    data-testid={`nav-${link.label}`}
                  >
                    {link.label}
                  </a>

                  <AnimatePresence>
                    {link.mega && activeMega === link.mega && (
                      <motion.div
                        key={link.mega}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.22, ease: "easeOut" }}
                        className="absolute left-1/2 top-full z-50 -translate-x-1/2 pt-3"
                      >
                        <div className="rounded-2xl border border-ink/5 bg-white/95 p-4 shadow-[0_20px_60px_rgba(0,0,0,0.12)] backdrop-blur-xl">
                          <MegaPanel type={link.mega} />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </nav>

          {/* Right */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setOpen(true)}
              className="hidden btn-primary !px-6 !py-3 text-sm md:inline-flex"
              data-testid="navbar-demo-btn"
            >
              Demo Talebi Oluştur
            </button>
            <button
              onClick={() => setMobileOpen(true)}
              aria-label="Menüyü aç"
              className={`inline-flex h-11 w-11 items-center justify-center rounded-full lg:hidden ${
                scrolled ? "text-ink" : "text-white"
              }`}
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] lg:hidden"
            data-testid="mobile-menu"
          >
            <div
              className="absolute inset-0 bg-ink/40 backdrop-blur-sm"
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", ease: [0.22, 1, 0.36, 1], duration: 0.4 }}
              className="absolute right-0 top-0 flex h-full w-[82%] max-w-sm flex-col bg-white p-6"
            >
              <div className="mb-8 flex items-center justify-between">
                <img src="/logo.png" alt="TEKNO ŞURA" className="h-9 w-auto" />
                <button
                  onClick={() => setMobileOpen(false)}
                  aria-label="Menüyü kapat"
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-cloud text-ink"
                  data-testid="mobile-menu-close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <nav className="flex flex-col gap-1">
                {NAV_LINKS.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    onClick={() => setMobileOpen(false)}
                    className="rounded-xl px-4 py-3.5 text-lg font-semibold text-ink transition-colors hover:bg-cloud"
                    data-testid={`mobile-nav-${link.label}`}
                  >
                    {link.label}
                  </a>
                ))}
              </nav>
              <div className="mt-auto space-y-3 pt-6">
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    setOpen(true);
                  }}
                  className="btn-primary w-full"
                  data-testid="mobile-demo-btn"
                >
                  Demo Talebi Oluştur
                </button>
                <a href={`tel:${COMPANY.phoneRaw}`} className="btn-secondary w-full">
                  <Phone className="h-4 w-4" /> {COMPANY.phone}
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
